import {useState} from 'react'
import type {ReactNode} from 'react'
import {useWorkspace} from '../../../contexts/WorkspaceContext'
import {AGENT_USER_ID} from '@/const/constants'
import {getMemberDisplayName} from '@/utils/memberDisplayName'
import Avatar from '../../ui/Avatar/Avatar'
import Badge from '@/components/ui/Badge/Badge'
import DropdownWrapper from '@/components/ui/Dropdown/components/DropdownWrapper/DropdownWrapper'
import Dropdown from '../../ui/Dropdown/Dropdown'
import DropdownItem from '@/components/ui/Dropdown/components/DropdownItem/DropdownItem'
import DropdownDivider from '@/components/ui/Dropdown/components/DropdownDivider/DropdownDivider'
import CheckIcon from '../../../assets/icons/icon-check.svg?react'

interface AssigneeDropdownProps {
    assigneeId: string | null
    onSelect: (assigneeId: string | null) => void
    children: (props: {onClick: () => void; isOpen: boolean}) => ReactNode
}

const AssigneeDropdown = ({assigneeId, onSelect, children}: AssigneeDropdownProps) => {
    const {members} = useWorkspace()
    const [isOpen, setIsOpen] = useState(false)

    const activeMembers = members.filter(m => m.status !== 'suspended')
    // The agent is separated from people, pinned to the bottom with an "Agent" badge.
    const humanMembers = activeMembers.filter(m => m.userId !== AGENT_USER_ID)
    const agentMember = activeMembers.find(m => m.userId === AGENT_USER_ID)

    const handleSelect = (userId: string | null) => {
        setIsOpen(false)
        onSelect(userId)
    }

    const agentRight = (
        <span style={{display: 'flex', alignItems: 'center', gap: 8}}>
            {agentMember?.userId === assigneeId && <CheckIcon/>}
            <Badge>Agent</Badge>
        </span>
    )

    return (
        <DropdownWrapper>
            {children({onClick: () => setIsOpen(!isOpen), isOpen})}
            <Dropdown isOpen={isOpen} onClose={() => setIsOpen(false)}>
                <DropdownItem
                    title="Unassigned"
                    rightIcon={assigneeId === null ? <CheckIcon/> : undefined}
                    onClick={() => handleSelect(null)}
                />
                {humanMembers.map((member) => (
                    <DropdownItem
                        key={member.userId}
                        icon={
                            <Avatar
                                title={member.username}
                                color={member.color}
                                avatar={member.avatarImage}
                            />
                        }
                        title={member.username}
                        rightIcon={member.userId === assigneeId ? <CheckIcon/> : undefined}
                        onClick={() => handleSelect(member.userId)}
                    />
                ))}
                {agentMember && (
                    <>
                        <DropdownDivider/>
                        <DropdownItem
                            icon={
                                <Avatar
                                    title={agentMember.username}
                                    color={agentMember.color}
                                    avatar={agentMember.avatarImage}
                                />
                            }
                            title={getMemberDisplayName(agentMember)}
                            rightIcon={agentRight}
                            onClick={() => handleSelect(agentMember.userId)}
                        />
                    </>
                )}
            </Dropdown>
        </DropdownWrapper>
    )
}

export default AssigneeDropdown
